import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SortSelect, sortData } from '../components/SortSelect';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { exportTableToPDF } from '../utils/html2pdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const priorityColors = {
  low: 'bg-green-500/20 text-green-400 border-green-500/50',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  urgent: 'bg-red-500/20 text-red-400 border-red-500/50',
  very_urgent: 'bg-pink-500/20 text-pink-400 border-pink-500/50'
};

const statusColors = {
  not_started: 'bg-gray-500/20 text-gray-400 border-gray-500/50',
  planned: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
  in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  project_work: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
  tender_phase: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
  completed: 'bg-green-500/20 text-green-400 border-green-500/50'
};

const statusLabels = {
  not_started: 'Baslanmadi',
  planned: 'Planlanan',
  in_progress: 'Devam Ediyor',
  project_work: 'Proje Isleri',
  tender_phase: 'Ihale Asamasinda',
  completed: 'Tamamlandi'
};

const priorityLabels = {
  low: 'Dusuk',
  medium: 'Orta',
  high: 'Yuksek',
  urgent: 'Acil',
  very_urgent: 'Cok Acil'
};

const TasksPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('newest');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [statusUpdateText, setStatusUpdateText] = useState('');
  const [viewUpdatesDialogOpen, setViewUpdatesDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentData, setAssignmentData] = useState({
    assigned_to_list: []
  });
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [pdfPageSize, setPdfPageSize] = useState('a4');

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []); // eslint-disable-line

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks?archived=false`, {
        withCredentials: true
      });
      // Extra client-side filter: exclude archived tasks and completed tasks
      const activeTasks = response.data.filter(task => 
        !task.archived && task.status !== 'completed'
      );
      console.log(`📋 Total tasks from API: ${response.data.length}, Active (non-completed): ${activeTasks.length}`);
      setTasks(activeTasks);
    } catch (error) {
      console.error('Tasks fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Users fetch error:', error);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskToDelete.id}`, {
        withCredentials: true
      });
      setTasks(tasks.filter(t => t.id !== taskToDelete.id));
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error('Delete error:', error);
      alert('Görev silinirken hata oluştu');
    }
  };

  const handleAddStatusUpdate = async () => {
    if (!selectedTask || !statusUpdateText.trim()) return;
    
    try {
      await axios.post(
        `${API}/tasks/${selectedTask.id}/status-update`,
        { update_text: statusUpdateText },
        { withCredentials: true }
      );
      
      // Refresh tasks
      await fetchTasks();
      
      // Close dialog and reset
      setStatusUpdateDialogOpen(false);
      setSelectedTask(null);
      setStatusUpdateText('');
    } catch (error) {
      console.error('Status update error:', error);
      alert('Son durum eklenirken hata oluştu');
    }
  };

  const openStatusUpdateDialog = (task) => {
    setSelectedTask(task);
    setStatusUpdateText('');
    setStatusUpdateDialogOpen(true);
  };

  const openViewUpdatesDialog = (task) => {
    setSelectedTask(task);
    setViewUpdatesDialogOpen(true);
  };


  const openAssignmentDialog = (task) => {
    setSelectedTask(task);
    setAssignmentData({
      assigned_to_list: task.assigned_to_list || [task.assigned_to, task.assigned_to_2, task.assigned_to_3].filter(Boolean)
    });
    setAssignmentDialogOpen(true);
  };

  const handleUpdateAssignments = async () => {
    try {
      await axios.patch(
        `${API}/tasks/${selectedTask.id}/update-assignments`,
        {
          ...assignmentData,
          assigned_to: assignmentData.assigned_to_list[0] || '',
          assigned_to_2: assignmentData.assigned_to_list[1] || '',
          assigned_to_3: assignmentData.assigned_to_list[2] || ''
        },
        { withCredentials: true }
      );
      setAssignmentDialogOpen(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Update assignments error:', error);
      alert('Atananları güncellerken hata oluştu');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleStatusChange = async (taskId, newStatus) => {
    if (!window.confirm(`Durumu "${statusLabels[newStatus]}" olarak değiştirmek istediğinizden emin misiniz?`)) {
      return;
    }
    
    try {
      await axios.patch(
        `${API}/tasks/${taskId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      // Show success message
      alert('✅ Durum başarıyla güncellendi!');
      
      // Refresh tasks
      await fetchTasks();
    } catch (error) {
      console.error('Status update error:', error);
      alert('❌ Durum güncellenirken hata oluştu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const exportFilteredTasksAsPDF = async () => {
    if (selectedColumns.length === 0) {
      alert('Lütfen en az bir sütun seçin');
      return;
    }

    const columnMapping = {
      'title': 'Görev',
      'description': 'Açıklama',
      'assigned_to': 'Atanan',
      'assigned_to_2': 'İkinci Atanan',
      'assigned_to_3': 'Üçüncü Atanan',
      'status': 'Durum',
      'priority': 'Öncelik',
      'start_date': 'Başlangıç',
      'due_date': 'Son Tarih',
      'deadline_date': 'Termin',
      'last_status_update': 'Son Durum'
    };

    const headers = ['S.NO', ...selectedColumns.map(col => columnMapping[col] || col)];
    
    const tasksData = filteredTasks.map((task, idx) => [
      idx + 1,
      ...selectedColumns.map(col => {
        if (col === 'title') return task.title || '';
        if (col === 'description') return (task.description || '').substring(0, 100);
        if (col === 'assigned_to') return task.assigned_to_email || '';
        if (col === 'assigned_to_2') return task.assigned_to_2_email || '';
        if (col === 'assigned_to_3') return task.assigned_to_3_email || '';
        if (col === 'status') return statusLabels[task.status] || task.status;
        if (col === 'priority') return priorityLabels[task.priority] || task.priority;
        if (col === 'start_date') return new Date(task.start_date).toLocaleDateString('tr-TR');
        if (col === 'due_date') return new Date(task.due_date).toLocaleDateString('tr-TR');
        if (col === 'deadline_date') return new Date(task.deadline_date).toLocaleDateString('tr-TR');
        if (col === 'last_status_update') {
          if (task.status_updates && task.status_updates.length > 0) {
            const lastUpdate = task.status_updates[task.status_updates.length - 1];
            return (lastUpdate.update_text || '').substring(0, 100);
          }
          return '-';
        }
        return '';
      })
    ]);

    let filterInfo = '';
    if (filterStatus !== 'all') filterInfo += `Durum: ${statusLabels[filterStatus]} | `;
    if (filterPriority !== 'all') filterInfo += `Öncelik: ${priorityLabels[filterPriority]} | `;
    if (filterUser !== 'all') {
      const selectedUser = users.find(u => u.id === filterUser);
      if (selectedUser) filterInfo += `Kullanıcı: ${selectedUser.name}`;
    }

    await exportTableToPDF(headers, tasksData, 'Görevler', `gorevler-${new Date().getTime()}.pdf`, {
      subtitle: filterInfo || undefined,
      pageSize: pdfPageSize
    });
    setPdfDialogOpen(false);
  };

  const isOverdue = (dueDate) => {
    return new Date(dueDate) < new Date();
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus !== 'all' && task.status !== filterStatus) return false;
    if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
    if (filterUser !== 'all' && 
        task.assigned_to !== filterUser && 
        task.assigned_to_2 !== filterUser && 
        task.assigned_to_3 !== filterUser) {
      return false;
    }
    if (filterDepartment !== 'all') {
      // Department filtresinde o departmandaki kullanıcılara atanan görevleri göster
      const deptUserIds = users.filter(u => u.department === filterDepartment).map(u => u.id);
      const taskUserIds = [task.assigned_to, task.assigned_to_2, task.assigned_to_3].filter(Boolean);
      if (!taskUserIds.some(id => deptUserIds.includes(id))) {
        return false;
      }
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Header */}
      <header className="bg-gray-900/50 border-b border-gray-800 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img src="/logo.png" alt="Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu Logo" className="w-12 h-12 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
              <div>
                <h1 className="text-xl font-bold text-white">Kurumsal İş Yönetimi, Takip ve Koordinasyon Platformu</h1>
                <p className="text-xs text-gray-400">Yatırım ve İnşaat</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="anasayfa-nav-button"
              >
                Anasayfa
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                data-testid="dashboard-nav-button"
              >
                Dashboard
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/tasks')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors border-b-2 border-blue-500"
              >
                Gorevler
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => navigate('/calendar')}
                className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
              >
                Takvim
              </button>
              {(user?.is_admin || user?.is_admin_assistant) && (
                <>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={() => navigate('/admin/users')}
                    className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800/50 rounded-lg transition-colors"
                    data-testid="users-nav-button"
                  >
                    Kullanicilar
                  </button>
                </>
              )}
              <div className="ml-4 border-l border-gray-700 pl-4 flex items-center space-x-4">
                <div 
                  className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <img src={user?.picture} alt={user?.name} className="w-8 h-8 rounded-full" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-gray-400">
                      {user?.is_admin ? 'Admin' : user?.is_admin_assistant ? 'Admin Yardımcısı' : user?.is_viewer ? 'İzleyici' : 'Kullanıcı'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="text-red-400 hover:text-red-300"
                  data-testid="logout-button"
                >
                  Cikis
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Görevler</h2>
            <p className="text-gray-400">
              {user?.is_admin ? 'Tüm görevleri görüntüleyin ve yönetin' : 'Size atanan görevler'}
            </p>
          </div>
          {(user?.is_admin || user?.is_admin_assistant) && (
            <Button
              onClick={() => navigate('/tasks/new')}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="create-new-task-button"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {user?.is_admin ? 'Yeni Görev' : 'Görev Öner (Onay Gerekli)'}
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6 items-center flex-wrap">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48 bg-gray-900 border-gray-800 text-white" data-testid="status-filter">
              <SelectValue placeholder="Durum Filtrele" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800 text-white">
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="not_started">Başlanmadı</SelectItem>
              <SelectItem value="planned">Planlanan</SelectItem>
              <SelectItem value="in_progress">Devam Ediyor</SelectItem>
              <SelectItem value="project_work">Proje İşleri</SelectItem>
              <SelectItem value="tender_phase">İhale Aşamasında</SelectItem>
              <SelectItem value="completed">Tamamlandı</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterPriority} onValueChange={setFilterPriority}>
            <SelectTrigger className="w-48 bg-gray-900 border-gray-800 text-white" data-testid="priority-filter">
              <SelectValue placeholder="Öncelik Filtrele" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800 text-white">
              <SelectItem value="all">Tüm Öncelikler</SelectItem>
              <SelectItem value="low">Düşük</SelectItem>
              <SelectItem value="medium">Orta</SelectItem>
              <SelectItem value="high">Yüksek</SelectItem>
              <SelectItem value="urgent">Acil</SelectItem>
              <SelectItem value="very_urgent">Çok Acil</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterUser} onValueChange={setFilterUser}>
            <SelectTrigger className="w-48 bg-gray-900 border-gray-800 text-white" data-testid="user-filter">
              <SelectValue placeholder="Kullanıcı Filtrele" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800 text-white">
              <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {user?.is_admin && (
            <Select value={filterDepartment} onValueChange={setFilterDepartment}>
              <SelectTrigger className="w-56 bg-gray-900 border-gray-800 text-white">
                <SelectValue placeholder="Alt Birim Filtrele" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800 text-white">
                <SelectItem value="all">Tüm Alt Birimler</SelectItem>
                <SelectItem value="bakim_onarim">Bakım-Onarım</SelectItem>
                <SelectItem value="emlak_kamulastirma">Emlak ve Kamulaştırma</SelectItem>
                <SelectItem value="yatirim_insaat">Yatırım ve İnşaat</SelectItem>
              </SelectContent>
            </Select>
          )}

          <Button
            onClick={() => setPdfDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white ml-auto"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Filtrelenmiş Listeyi PDF Olarak İndir ({filteredTasks.length} görev)
          </Button>
        </div>

        {/* Tasks Grid */}
        {filteredTasks.length === 0 ? (
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-12 text-center">
              <p className="text-gray-400">Henüz görev yok</p>
            </CardContent>
          </Card>
        ) : (
          <>
          <div className="flex justify-end mb-3">
            <SortSelect value={sortKey} onChange={setSortKey} options={['newest','oldest','updated','name_asc','name_desc']} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tasks-grid">
            {sortData(filteredTasks, sortKey, 'title', null).map((task) => (
              <Card
                key={task.id}
                className={`bg-gray-900 border-gray-800 hover:border-gray-700 transition-all cursor-pointer ${
                  task.status !== 'completed' && isOverdue(task.due_date)
                    ? 'ring-2 ring-red-500/50'
                    : ''
                }`}
                onClick={() => openViewUpdatesDialog(task)}
                data-testid={`task-card-${task.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-white text-lg">{task.title}</CardTitle>
                    <Badge className={statusColors[task.status]}>
                      {statusLabels[task.status]}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-400 mt-2 line-clamp-2">{task.description}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Priority and Dates */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Öncelik:</span>
                      <Badge className={priorityColors[task.priority]} variant="outline">
                        {priorityLabels[task.priority]}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Başlangıç:</span>
                      <span className="text-gray-400">{new Date(task.start_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Son Tarih:</span>
                      <span className={`${
                        task.status !== 'completed' && isOverdue(task.due_date)
                          ? 'text-red-400 font-medium'
                          : 'text-gray-400'
                      }`}>
                        {new Date(task.due_date).toLocaleDateString('tr-TR')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Termin:</span>
                      <span className="text-gray-400">{new Date(task.deadline_date).toLocaleDateString('tr-TR')}</span>
                    </div>
                  </div>

                  {/* Assigned to */}
                  <div className="pt-2 border-t border-gray-800">
                    <p className="text-xs text-gray-500 mb-1">Atanan ({(task.assigned_to_names || task.assigned_to_list || []).length} kişi):</p>
                    {(task.assigned_to_names && task.assigned_to_names.length > 0) ? (
                      task.assigned_to_names.map((person, idx) => (
                        <p key={person.id || `name-${idx}`} className="text-sm text-gray-300">{person.name || person.email}</p>
                      ))
                    ) : (task.assigned_to_list && task.assigned_to_list.length > 0) ? (
                      task.assigned_to_list.map((uid, idx) => {
                        const u = users.find(u => u.id === uid);
                        return <p key={uid || `uid-${idx}`} className="text-sm text-gray-300">{u ? u.name : uid}</p>;
                      })
                    ) : (
                      <>
                        <p className="text-sm text-gray-300">{task.assigned_to_email || '-'}</p>
                        {task.assigned_to_2_email && <p className="text-sm text-gray-300">{task.assigned_to_2_email}</p>}
                        {task.assigned_to_3_email && <p className="text-sm text-gray-300">{task.assigned_to_3_email}</p>}
                      </>
                    )}
                  </div>

                  {/* Status Change (Admin & Admin Assistant) */}
                  {(user?.is_admin || user?.is_admin_assistant) && (
                    <div className="pt-2 border-t border-gray-800">
                      <Label className="text-xs text-gray-500 mb-2 block">Durum Güncelle:</Label>
                      <Select
                        value={task.status}
                        onValueChange={(value) => handleStatusChange(task.id, value)}
                      >
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white text-xs h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-800 text-white">
                          <SelectItem value="not_started">Başlanmadı</SelectItem>
                          <SelectItem value="planned">Planlanan</SelectItem>
                          <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                          <SelectItem value="project_work">Proje İşleri</SelectItem>
                          <SelectItem value="tender_phase">İhale Aşamasında</SelectItem>
                          <SelectItem value="completed">Tamamlandı</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    {user?.is_admin && (
                      <>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/tasks/edit/${task.id}`);
                          }}
                          size="sm"
                          variant="outline"
                          className="flex-1 bg-gray-800 border-gray-700 text-white hover:bg-gray-700 text-xs"
                          data-testid={`edit-task-${task.id}`}
                        >
                          Düzenle
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTaskToDelete(task);
                            setDeleteDialogOpen(true);
                          }}
                          size="sm"
                          variant="outline"
                          className="flex-1 bg-red-900/20 border-red-700 text-red-400 hover:bg-red-900/40 text-xs"
                          data-testid={`delete-task-${task.id}`}
                        >
                          Sil
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {(user?.is_admin_assistant || task.assigned_to === user?.id || task.assigned_to_2 === user?.id || task.assigned_to_3 === user?.id) && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openStatusUpdateDialog(task);
                        }}
                        size="sm"
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                        data-testid={`add-status-update-${task.id}`}
                      >
                        Son Durum Ekle
                      </Button>
                    )}
                    {user?.is_admin_assistant && !user?.is_admin && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openAssignmentDialog(task);
                        }}
                        size="sm"
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs"
                      >
                        Atananları Düzenle
                      </Button>
                    )}
                    {task.status_updates && task.status_updates.length > 0 && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          openViewUpdatesDialog(task);
                        }}
                        size="sm"
                        variant="outline"
                        className="flex-1 bg-gray-800 border-gray-700 text-white hover:bg-gray-700 text-xs"
                        data-testid={`view-updates-${task.id}`}
                      >
                        Son Durumlar ({task.status_updates.length})
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          </>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-gray-900 border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Görevi Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Bu görevi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-white hover:bg-gray-700">
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTask}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateDialogOpen} onOpenChange={setStatusUpdateDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Son Durum Ekle</DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedTask?.title} görevi için son durum güncellemesi ekleyin
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="status-update" className="text-white mb-2 block">Durum Güncellemesi</Label>
            <Textarea
              id="status-update"
              value={statusUpdateText}
              onChange={(e) => setStatusUpdateText(e.target.value)}
              placeholder="Görevle ilgili son durumu buraya yazın..."
              className="bg-gray-800 border-gray-700 text-white min-h-[100px]"
            />
          </div>
          <DialogFooter>
            <Button
              onClick={() => setStatusUpdateDialogOpen(false)}
              variant="ghost"
              className="text-gray-400"
            >
              İptal
            </Button>
            <Button
              onClick={handleAddStatusUpdate}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Ekle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Updates Dialog */}
      <Dialog open={viewUpdatesDialogOpen} onOpenChange={setViewUpdatesDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTask?.title}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedTask?.description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Task Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Durum</p>
                <Badge className={statusColors[selectedTask?.status]}>
                  {statusLabels[selectedTask?.status]}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-gray-500">Öncelik</p>
                <Badge className={priorityColors[selectedTask?.priority]}>
                  {priorityLabels[selectedTask?.priority]}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-gray-500">Atanan</p>
                <p className="text-sm text-white">{selectedTask?.assigned_to_email}</p>
                {selectedTask?.assigned_to_2_email && (
                  <p className="text-sm text-white mt-1">{selectedTask?.assigned_to_2_email}</p>
                )}
                {selectedTask?.assigned_to_3_email && (
                  <p className="text-sm text-white mt-1">{selectedTask?.assigned_to_3_email}</p>
                )}
              </div>
            </div>

            {/* Status Updates */}
            {selectedTask?.status_updates && selectedTask.status_updates.length > 0 && (
              <div>
                <h3 className="font-medium text-white mb-3">Son Durum Güncellemeleri</h3>
                <div className="space-y-3">
                  {selectedTask.status_updates.slice().reverse().map((update, index) => (
                    <div key={`${update.created_at}-${index}`} className="bg-gray-800 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-blue-400">{update.updated_by_name}</span>
                        <span className="text-xs text-gray-500">{new Date(update.created_at).toLocaleDateString('tr-TR')} {new Date(update.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p className="text-gray-300 text-sm">{update.update_text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setViewUpdatesDialogOpen(false)}
              className="bg-gray-800 text-white hover:bg-gray-700"
            >
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Atananları Düzenle Dialog (Admin Assistant Only) */}
      <Dialog open={assignmentDialogOpen} onOpenChange={setAssignmentDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle>Atananları Düzenle</DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedTask?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white">Atanan Kişiler ({assignmentData.assigned_to_list?.length || 0} kişi)</Label>
              <div className="grid grid-cols-2 gap-2 p-3 bg-gray-800/50 rounded-lg border border-gray-700 max-h-48 overflow-y-auto">
                {users.map(u => {
                  const isSelected = (assignmentData.assigned_to_list || []).includes(u.id);
                  return (
                    <button key={u.id} type="button"
                      onClick={() => {
                        const list = isSelected
                          ? assignmentData.assigned_to_list.filter(id => id !== u.id)
                          : [...(assignmentData.assigned_to_list || []), u.id];
                        setAssignmentData({ ...assignmentData, assigned_to_list: list });
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${isSelected ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-white border-white' : 'border-gray-500'}`}>
                        {isSelected && <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      <span className="truncate">{u.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setAssignmentDialogOpen(false)}
              variant="ghost"
              className="text-gray-400"
            >
              İptal
            </Button>
            <Button
              onClick={handleUpdateAssignments}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Atananları Güncelle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Sütun Seçimi Dialog */}
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>PDF İçin Sütun Seçin</DialogTitle>
            <DialogDescription className="text-gray-400">
              {filteredTasks.length} görev için PDF raporunda görmek istediğiniz sütunları seçin
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 py-4" key={selectedColumns.join(',')}>
            {[
              {id: 'title', label: 'Görev Başlığı'},
              {id: 'description', label: 'Açıklama'},
              {id: 'assigned_to', label: 'Atanan Kişi'},
              {id: 'assigned_to_2', label: 'İkinci Atanan'},
              {id: 'assigned_to_3', label: 'Üçüncü Atanan'},
              {id: 'status', label: 'Durum'},
              {id: 'priority', label: 'Öncelik'},
              {id: 'start_date', label: 'Başlangıç Tarihi'},
              {id: 'due_date', label: 'Son Tarih'},
              {id: 'deadline_date', label: 'Termin Tarihi'},
              {id: 'last_status_update', label: 'Son Durum Güncelleme'}
            ].map(col => (
              <div key={col.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`pdf_${col.id}`}
                  checked={selectedColumns.includes(col.id)}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelectedColumns([...selectedColumns, col.id]);
                    } else {
                      setSelectedColumns(selectedColumns.filter(c => c !== col.id));
                    }
                  }}
                  className="border-gray-600"
                />
                <label htmlFor={`pdf_${col.id}`} className="text-sm text-gray-300 cursor-pointer">
                  {col.label}
                </label>
              </div>
            ))}
          </div>
          <DialogFooter className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-1 mr-auto bg-gray-800 rounded-lg p-1">
              <Button size="sm" onClick={() => setPdfPageSize('a4')} className={`h-7 px-3 text-xs ${pdfPageSize === 'a4' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}>A4</Button>
              <Button size="sm" onClick={() => setPdfPageSize('a3')} className={`h-7 px-3 text-xs ${pdfPageSize === 'a3' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-400 hover:text-white'}`}>A3</Button>
            </div>
            <Button 
              onClick={() => {
                setSelectedColumns(['title', 'description', 'assigned_to', 'assigned_to_2', 'assigned_to_3', 'status', 'priority', 'start_date', 'due_date', 'deadline_date', 'last_status_update']);
              }} 
              variant="outline" 
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Tümünü Seç (11 Sütun)
            </Button>
            <Button 
              onClick={() => {
                setSelectedColumns(['title', 'assigned_to', 'status', 'priority', 'due_date']);
              }} 
              variant="outline" 
              className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            >
              Hızlı Seçim (5 Temel)
            </Button>
            <Button onClick={() => setPdfDialogOpen(false)} variant="ghost" className="text-gray-400">
              İptal
            </Button>
            <Button onClick={exportFilteredTasksAsPDF} className="bg-blue-600 hover:bg-blue-700 text-white">
              PDF Oluştur ({pdfPageSize.toUpperCase()})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default TasksPage;